/**
 * <free-shipping-booster>
 * Adds one booster variant to the cart, then re-renders the cart surfaces in place.
 * Re-upgrades automatically when a cart section is re-rendered.
 */
class FreeShippingBooster extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-fsb-add]');
    this.errorEl = this.querySelector('[data-fsb-error]');
    if (this.button) {
      this.button.addEventListener('click', this.onAdd.bind(this));
    }
  }

  get addUrl() {
    // theme.routes.cart_add_url is the FORM endpoint (/cart/add) and returns HTML.
    // The AJAX endpoint needs the .js suffix or the response cannot be parsed.
    const base =
      (window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add';
    return base.endsWith('.js') ? base : base + '.js';
  }

  // The drawer also contains an element with id="main-cart-items", so page
  // detection has to be based on where THIS card actually lives.
  inDrawer() {
    return !!this.closest('.mini-cart, #mini-cart');
  }

  sectionsToRequest() {
    if (this.inDrawer()) {
      return ['mini-cart', 'cart-icon-bubble', 'mobile-cart-icon-bubble'];
    }
    const items = document.getElementById('main-cart-items');
    const footer = document.getElementById('main-cart-footer');
    return [
      items && items.dataset.id,
      footer && footer.dataset.id,
      'cart-icon-bubble',
      'mobile-cart-icon-bubble'
    ].filter(Boolean);
  }

  setBusy(busy) {
    this.busy = busy;
    this.setAttribute('aria-busy', busy ? 'true' : 'false');
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.textContent = busy
      ? this.button.dataset.labelBusy
      : this.button.dataset.labelIdle;
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.classList.remove('visually-hidden');
  }

  onAdd() {
    if (this.busy) return;
    this.setBusy(true);
    if (this.errorEl) this.errorEl.textContent = '';

    const body = JSON.stringify({
      items: [{ id: Number(this.dataset.variantId), quantity: 1 }],
      sections: this.sectionsToRequest(),
      sections_url: window.location.pathname
    });

    fetch(this.addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript'
      },
      body: body
    })
      .then((response) =>
        response.text().then((text) => {
          try {
            return JSON.parse(text);
          } catch (e) {
            throw new Error('Cart did not return JSON (HTTP ' + response.status + ')');
          }
        })
      )
      .then((state) => {
        if (state.status || state.description) {
          throw new Error(state.description || state.message || 'Could not add item');
        }
        this.render(state);
      })
      .catch((error) => {
        console.error('[free-shipping-booster]', error);
        this.setBusy(false);
        this.showError('Sorry, we could not add that. Please try again.');
      });
  }

  render(state) {
    const sections = state.sections || {};

    if (this.inDrawer()) {
      const drawer = document.querySelector('mini-cart');
      if (drawer && typeof drawer.renderContents === 'function') {
        drawer.renderContents(state);
        return;
      }
      window.location.reload();
      return;
    }

    const footer = document.getElementById('main-cart-footer');
    if (!footer) {
      window.location.reload();
      return;
    }

    this.replaceById('main-cart-items', sections);
    this.replaceById('main-cart-footer', sections);
    this.replaceSection('cart-icon-bubble', sections['cart-icon-bubble']);
    this.replaceSection('mobile-cart-icon-bubble', sections['mobile-cart-icon-bubble']);

    // If this card survived the re-render, the swap did not take — reload rather
    // than leave the button stuck.
    if (document.contains(this)) window.location.reload();
  }

  // Cart page: swap a whole section wrapper's contents, keyed by its real section id.
  replaceById(elementId, sections) {
    const element = document.getElementById(elementId);
    if (!element) return;
    const html = sections[element.dataset.id];
    if (!html) return;
    const fresh = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#' + elementId);
    if (fresh) element.innerHTML = fresh.innerHTML;
  }

  replaceSection(elementId, html) {
    const element = document.getElementById(elementId);
    if (!element || !html) return;
    const fresh = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('.shopify-section');
    if (fresh) element.innerHTML = fresh.innerHTML;
  }
}

if (!customElements.get('free-shipping-booster')) {
  customElements.define('free-shipping-booster', FreeShippingBooster);
}